import React, { useCallback, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { Modal, Popconfirm, message } from "antd";
import Header from "../../CoreComponents/Header";
import MainHeader from "../../CoreComponents/MainHeader";
import NavBar from "../../CoreComponents/NavBar";
import "../Settlements/settlements.css";
import { is_superuser } from "../../Functionalities/Config";
import {
  activateUpdate,
  devicePills,
  fetchOverview,
  offerUpdate,
  rollbackToBuiltIn,
  servedLine,
  setPaused,
  shortId,
  versionsSorted,
  APP_CHOICES,
  publishProblem,
  publishUpdate,
} from "./logic";

const lastSeen = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "never");

const Pills = ({ devices }) => (
  <div className="d-flex flex-wrap mt-2">
    {devicePills(devices).map((pill) => (
      <span key={pill.key} className="badge badge-light mr-2 mb-1" style={{ fontSize: 12 }}>
        {pill.label}: <strong>{pill.value}</strong>
      </span>
    ))}
  </div>
);

const VersionRow = ({ app, channel, runtime, onAct, onOffer }) => {
  const [open, setOpen] = useState(false);
  const earlier = (runtime.updates || []).filter((u) => !u.is_current);
  return (
    <div className="border-top py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-start">
        <div>
          <h5 className="mb-1">Version {runtime.runtime_version}</h5>
          <div className="text-muted">{servedLine(runtime)}</div>
          {runtime.current && runtime.current.also_offered_to && runtime.current.also_offered_to.length > 0 && (
            <div className="text-muted small">Also offered to {runtime.current.also_offered_to.join(", ")}</div>
          )}
          <Pills devices={runtime.devices} />
          <div className="text-muted small">Last seen {lastSeen(runtime.devices && runtime.devices.last_seen)}</div>
        </div>
        <div className="d-flex flex-wrap">
          <Popconfirm
            title={`Send phones on ${runtime.runtime_version} back to the built-in bundle?`}
            okText="Roll back"
            onConfirm={() => onAct(() => rollbackToBuiltIn(app.app, channel.channel, runtime.runtime_version))}
          >
            <button type="button" className="btn btn-outline-primary btn-sm mr-2 mb-1">Roll back to built-in</button>
          </Popconfirm>
          {runtime.current && (
            <button type="button" className="btn btn-outline-primary btn-sm mr-2 mb-1" onClick={() => onOffer(runtime, channel)}>
              Offer to another version...
            </button>
          )}
          {earlier.length > 0 && (
            <button type="button" className="btn btn-outline-primary btn-sm mb-1" onClick={() => setOpen(!open)}>
              {open ? "Hide" : "Show"} earlier updates ({earlier.length})
            </button>
          )}
        </div>
      </div>
      {open &&
        earlier.map((update) => (
          <div key={update.update_id} className="d-flex flex-wrap justify-content-between align-items-center mt-2 pl-3">
            <div className="text-muted">
              {update.message ? `${update.message} · ` : ""}
              {lastSeen(update.created_at)} · {shortId(update.update_id)} · {(update.platforms || []).join(", ")}
            </div>
            <Popconfirm title="Make this update current again?" okText="Make current" onConfirm={() => onAct(() => activateUpdate(update.update_id))}>
              <button type="button" className="btn btn-primary btn-sm">Make current</button>
            </Popconfirm>
          </div>
        ))}
    </div>
  );
};

export default function AppUpdates() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [version, setVersion] = useState("");
  const [publishing, setPublishing] = useState(null);
  const [publishError, setPublishError] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const body = await fetchOverview();
    setLoading(false);
    if (body && body.Status === true) setOverview(body);
    else message.error((body && body.Error) || "Could not load the app updates");
  }, []);

  useEffect(() => {
    if (is_superuser) load();
  }, [load]);

  if (!is_superuser) return <Redirect to="/orders" />;

  const act = async (call) => {
    const body = await call();
    if (body && body.Status === true) message.success(body.Message || "Done");
    else message.error((body && body.Error) || "That did not work");
    await load();
  };

  const sendOffer = async () => {
    const target = version.trim();
    if (!target) return;
    const { runtime, channel } = offer;
    setOffer(null);
    setVersion("");
    await act(() => offerUpdate(runtime.current.update_id, target, channel.channel));
  };

  const openPublish = () => {
    setPublishError("");
    setPublishing({ app: "rider", runtimeVersion: "", note: "", file: null });
  };

  const sendPublish = async () => {
    const problem = publishProblem(publishing);
    if (problem) {
      setPublishError(problem);
      return;
    }
    setSending(true);
    const body = await publishUpdate(publishing);
    setSending(false);
    if (body && body.Status === true) {
      message.success(body.Message || "Update published");
      setPublishing(null);
      await load();
    } else {
      setPublishError((body && body.Error) || "The upload did not go through");
    }
  };

  const totals = (overview && overview.totals) || {};

  return (
    <div id="main-wrapper">
      <MainHeader />
      <Header name={"App updates"} />
      <NavBar />
      <div className="content-body" style={{ minHeight: "100vh" }}>
        <div className="container-fluid settlements">
          <div className="d-flex justify-content-end mb-3">
            <button type="button" className="btn btn-primary" onClick={openPublish}>
              Publish update
            </button>
          </div>
          {loading && <p className="text-muted">Loading...</p>}
          {overview &&
            overview.apps.map((app) => {
              const total = totals[app.app] || {};
              return (
                <div className="card" key={app.app}>
                  <div className="card-header d-flex flex-wrap justify-content-between align-items-center">
                    <h4 className="card-title mb-0">{app.label}</h4>
                    <span className="text-muted">
                      {total.devices || 0} phones · last seen {lastSeen(total.last_seen)}
                    </span>
                  </div>
                  <div className="card-body">
                    {app.channels.length === 0 && <p className="text-muted mb-0">No updates published yet.</p>}
                    {app.channels.map((channel) => (
                      <div key={channel.channel} className="mb-4">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <h5 className="mb-0">
                            Channel {channel.channel}{" "}
                            {channel.paused && <span className="badge badge-warning ml-2">paused</span>}
                          </h5>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => act(() => setPaused(app.app, channel.channel, !channel.paused))}
                          >
                            {channel.paused ? "Resume" : "Pause"}
                          </button>
                        </div>
                        {versionsSorted(channel.runtimes).map((runtime) => (
                          <VersionRow
                            key={runtime.runtime_version}
                            app={app}
                            channel={channel}
                            runtime={runtime}
                            onAct={act}
                            onOffer={(r, c) => setOffer({ runtime: r, channel: c })}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      <Modal
        open={Boolean(offer)}
        title={offer ? `Offer ${shortId(offer.runtime.current.update_id)} to another version` : ""}
        okText="Offer"
        onOk={sendOffer}
        onCancel={() => setOffer(null)}
      >
        <input className="form-control mb-2" placeholder="App version, for example 3.1.3" value={version} onChange={(e) => setVersion(e.target.value)} />
        <p className="text-muted mb-0">Only for a version whose native code is the same. A version with a native change must get a store build instead.</p>
      </Modal>
      <Modal
        open={Boolean(publishing)}
        title="Publish an update"
        okText={sending ? "Uploading..." : "Publish"}
        okButtonProps={{ disabled: sending }}
        onOk={sendPublish}
        onCancel={() => !sending && setPublishing(null)}
      >
        {publishing && (
          <div>
            <label className="d-block mb-1">App</label>
            <select
              className="form-control mb-2"
              value={publishing.app}
              onChange={(e) => setPublishing({ ...publishing, app: e.target.value })}
            >
              {APP_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>{choice.label}</option>
              ))}
            </select>
            <label className="d-block mb-1">App version it is for</label>
            <input
              className="form-control mb-2"
              placeholder="3.1.2"
              value={publishing.runtimeVersion}
              onChange={(e) => setPublishing({ ...publishing, runtimeVersion: e.target.value })}
            />
            <label className="d-block mb-1">What changed</label>
            <input
              className="form-control mb-2"
              placeholder="Profile shows the running update"
              value={publishing.note}
              onChange={(e) => setPublishing({ ...publishing, note: e.target.value })}
            />
            <label className="d-block mb-1">Zip from the export script</label>
            <input
              type="file"
              accept=".zip"
              className="form-control mb-2"
              onChange={(e) => setPublishing({ ...publishing, file: e.target.files[0] || null })}
            />
            <p className="text-muted small mb-1">
              Make the zip in the app's folder with scripts/ota-export.sh, for the same server this panel uses.
              Every build listens on channel production, so the update reaches every phone on this server that runs
              the version above.
            </p>
            {publishError && <p className="text-danger mb-0">{publishError}</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}
